"use client";

import { ReactNode } from "react";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { Lock } from "lucide-react";

interface ViewerDisabledProps {
  children: ReactNode;
  /** Contextual upgrade message shown in tooltip (e.g. "Upgrade to call this lead") */
  message?: string;
  /** If true, completely hides the element instead of greying it out */
  hide?: boolean;
}

/**
 * Wraps children with a disabled overlay when in viewer mode.
 * Active users: renders children with zero overhead (single boolean check).
 * Viewers: renders greyed-out children with upgrade tooltip.
 */
export default function ViewerDisabled({ children, message, hide }: ViewerDisabledProps) {
  const { isViewerMode } = useTrialStatus();
  const { user } = useUserContext();

  // Active users — pass through immediately
  const isAdmin = user?.user?.userType === "admin";
  if (!isViewerMode || isAdmin) {
    return <>{children}</>;
  }

  // Hide mode — render nothing
  if (hide) return null;

  const tooltipMessage = message || "Upgrade to unlock this feature";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative inline-flex">
            <div className="opacity-40 pointer-events-none select-none" aria-disabled="true">
              {children}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg"
        >
          <Lock className="w-3 h-3 text-amber-400" />
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
